import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import type { Editor } from "@tiptap/react";
import type { FrontMatterFieldError, FrontMatterValidation } from "@workith/editor-schema";
import { validateFrontMatter } from "@workith/editor-schema";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useEffect, useReducer, useState } from "react";
import { focusAfterFrontMatter } from "./frontMatterCommands";

/**
 * 카드 오류는 중복 키 때문에 카드 전체를 함께 봐야 계산할 수 있다. 그렇다고 필드마다 카드를
 * 다시 검증하면 필드 수의 제곱만큼 YAML을 재파싱하게 된다. ProseMirror 노드는 불변이라
 * 노드 identity가 곧 카드 내용의 버전이므로, 카드가 실제로 바뀔 때만 계산해 공유한다.
 * (필드 NodeView는 카드와 별도 포털로 렌더돼 React context가 닿지 않아 캐시로 공유한다.)
 */
const validationCache = new WeakMap<ProseMirrorNode, FrontMatterValidation>();

export function frontMatterValidation(card: ProseMirrorNode): FrontMatterValidation {
  const cached = validationCache.get(card);
  if (cached) return cached;
  const validation = validateFrontMatter(card.toJSON());
  validationCache.set(card, validation);
  return validation;
}

export function frontMatterFieldErrors(
  card: ProseMirrorNode,
  fieldIndex: number
): FrontMatterFieldError[] {
  return frontMatterValidation(card).errors.filter((error) => error.fieldIndex === fieldIndex);
}

/**
 * 카드 안의 모든 값은 ProseMirror 문서가 소유한다. React state는 편집 가능 여부만 담는다.
 *
 * 문서가 바뀌면 다시 그려야 한다. 앞선 필드의 텍스트 길이가 바뀌면 뒤 필드의 위치가 밀리고,
 * 중복 키 오류는 다른 행의 변경으로도 생기므로 자기 노드만 봐서는 알 수 없기 때문이다.
 * 반대로 원격 캐럿 이동 같은 선택 전용 transaction에는 다시 그리지 않는다.
 * (`setEditable`은 transaction 없이 `update`만 emit하므로 두 이벤트를 모두 구독한다.)
 */
export function useFrontMatterEditorSync(editor: Editor): boolean {
  const [editable, setEditable] = useState(editor.isEditable);
  const [, redraw] = useReducer((revision: number) => revision + 1, 0);

  useEffect(() => {
    const syncEditable = () => setEditable(editor.isEditable);
    const handleTransaction = ({ transaction }: { transaction: Transaction }) => {
      syncEditable();
      if (transaction.docChanged) redraw();
    };
    syncEditable();
    editor.on("transaction", handleTransaction);
    editor.on("update", syncEditable);
    return () => {
      editor.off("transaction", handleTransaction);
      editor.off("update", syncEditable);
    };
  }, [editor]);

  return editable;
}

/**
 * Backspace/Delete는 컨트롤 안에서만 소비해 카드 전체가 지워지지 않게 한다.
 * Tab/Shift+Tab은 카드 안의 활성 컨트롤을 순회하고 경계를 넘으면 카드 뒤 본문으로 나간다.
 * 카드 자체가 NodeSelection된 상태의 삭제는 여기서 가로채지 않는다.
 */
export function handleFrontMatterControlKeyDown(
  event: ReactKeyboardEvent<HTMLElement>,
  editor: Editor,
  cardPos: number | undefined
): void {
  if (event.key === "Backspace" || event.key === "Delete") {
    event.stopPropagation();
    return;
  }
  if (event.key !== "Tab") return;

  const card = event.currentTarget.closest('[data-type="frontMatter"]');
  const controls = Array.from(
    card?.querySelectorAll<HTMLElement>("[data-frontmatter-control]:not([disabled])") ?? []
  );
  const next = controls[controls.indexOf(event.currentTarget) + (event.shiftKey ? -1 : 1)];
  event.preventDefault();
  if (next) {
    next.focus();
    return;
  }
  if (typeof cardPos === "number") {
    focusAfterFrontMatter(editor, cardPos);
  }
}
